import {MinimalOCPPTagDTO, OCPPTagDTO, Page, SearchRequestParams} from '../common';
import {LocationType, TitleEnum, UserTypeEnum} from 'src/enums';
import {AddressDTO, AuthUserDTO, UserRoleDTO} from '../auth';
import {CompanyDTO} from '../company';
import {EmployeeDTO} from '../employee';

export interface RealEstateDTO {
    id: number;
    name: string;
    createdAt: number;
}

export interface ActiveChargingLocationInfo {
    id: number;
    name: string;
    locationType: LocationType;
    latitude: number;
    longitude: number;
    timezone?: string;
    address: AddressDTO;
    realEstate: RealEstateDTO;
}

export interface MinimalUserDTO {
    id: number;
    firstName: string;
    lastName: string;
    userType: UserTypeEnum;
    title: TitleEnum;
    emailAddress: string;
    phoneNumber: string;
    chargePointId: string;
    roles: UserRoleDTO[];
    created: number;
    hasActiveContract: boolean;
    lastLogin: number;
    realEstate?: RealEstateDTO;
    defaultOcppTag?: MinimalOCPPTagDTO;
    company?: CompanyDTO;
    employee?: EmployeeDTO;
}

export interface TransactionSearchRequest {
    data: TransactionSearchRequestData;
    params?: SearchRequestParams;
}

export interface TransactionSearchRequestData {
    businessId?: number;
    companyId?: number;
    chargerId?: number;
    locationId?: number;
    startFrom?: number;
    startTo?: number;
    stopFrom?: number;
    stopTo?: number;
    userId?: number;
    tagId?: number;
    employeeId?: number;
    keyword?: string;
    realEstateId?: number;
    active?: boolean;
    invoiceId?: number;
    invoiced?: boolean;
}

export interface PageTransactionDTO extends Page {
    content: TransactionDTO[];
}

export interface TransactionConnectorDTO {
    id: number;
    connectorId: number;
    currentStatus: string;
    chargerId: number;
    chargePointId: string;
    chargerVendor: string;
    chargerModelName: string;
}

export interface TransactionDTO {
    id: number;
    startTimestamp: number;
    startValue: number;
    stopReason: string;
    stopTimestamp: number;
    stopValue: number;
    transactionSummary: TransactionSummaryDTO;
    idTag: MinimalOCPPTagDTO;
    user: MinimalUserDTO;
    cost?: TransactionCostDTO;
    connector: TransactionConnectorDTO;
    location: ActiveChargingLocationInfo;
    clientType?: ClientType;
}

export interface TransactionSummaryDTO {
    chargingTimeMinutes: number;
    connectedTimeMinutes: number;
    wattsConsumed: number;
}

export interface ActiveTransactionRequest {
    businessId: number;
    chargerId: number;
    connectorId: number;
    companyId?: number;
}

export interface ActiveTransactionDTO {
    amperage: string;
    chargingTimeMinutes: number;
    id: number;
    idTag: OCPPTagDTO;
    startTimeStamp: number;
    startValue: number;
    wattsConsumed: number;
    user: AuthUserDTO;
    cost?: TransactionCostDTO;
}

export interface ConnectorMeterValueDTO {
    id: number;
    value: string;
    valueTimestamp: number;
    unit: string;
    measurand: string;
}

export interface PriceSnapshotDTO {
    id: number;
    isDefault: boolean;
    itemPrice: number;
    energyType: string;
    month: string;
    dayOfWeek: string;
    hourOfDay: number; // 0-23
}

export interface TransactionCostItemDTO {
    id: number;
    itemCost: number;
    createdAt: number;
    itemTimestamp: number;
    previousConsumption: number;
    currentConsumption: number;
    itemConsumption: number;
    currentSessionConsumption: number;
    priceSnapshot?: PriceSnapshotDTO;
    meterValue?: ConnectorMeterValueDTO;
}

export interface TransactionCostDTO {
    id: number;
    totalCost: number;
    currency: string;
    totalConsumption: number;
    averagePrice?: number; // average price per kWh in 1/10000 cents (e.g., 12342 = 1.2342 CHF/kWh)
    createdAt: number;
    updatedAt: number;
}

export interface DetailedTransactionCostDTO extends TransactionCostDTO {
    items: TransactionCostItemDTO[];
}

export interface DetailedTransactionDTO {
    id: number;
    startTimestamp: number;
    startValue: number;
    stopReason: string;
    stopTimestamp: number;
    stopValue: number;
    transactionSummary: TransactionSummaryDTO;
    idTag: MinimalOCPPTagDTO;
    user: MinimalUserDTO;
    cost?: DetailedTransactionCostDTO;
    connector: TransactionConnectorDTO;
    location: ActiveChargingLocationInfo;
    connectorMeterValues: ConnectorMeterValueDTO[];
    clientType?: ClientType;
}

export enum ClientType {
    REGULAR = 'REGULAR',
    GUEST = 'GUEST',
}

export enum ExportFileType {
    CSV = 'CSV',
    PDF = 'PDF',
}

export interface TransactionExportRequest {
    data: TransactionExportRequestData;
}

export interface TransactionExportRequestData extends TransactionSearchRequestData {
    fileType: ExportFileType;
}

export interface CostItemExportRequest {
    transactionId: number;
    fileType: ExportFileType;
}
